import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import {
    Box,
    Button,
    Center,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Icon,
    useToast,
    Spinner,
    Alert,
    AlertIcon,
    Badge,
    Divider,
    SimpleGrid,
    Stack,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon
} from "@chakra-ui/react";
import { CheckCircleIcon, ArrowBackIcon, EditIcon } from "@chakra-ui/icons";
import { HiUser, HiMail, HiPhone, HiIdentification, HiCalendar, HiGlobeAlt, HiCreditCard, HiInformationCircle, HiQrcode, HiOfficeBuilding, HiDocumentText } from "react-icons/hi";
import { motion } from "framer-motion";
import moment from "moment";
import { useRouteConfig } from "./providers/RouteConfigProvider";

const MotionBox = motion(Box);

const InfoRow = ({ icon, label, value, ...props }) => (
    <HStack spacing={3} py={2} {...props}>
        {icon && <Icon as={icon} color="brand.500" boxSize={4} />}
        <Box flex="1">
            <Text fontSize="xs" color="gray.500" fontWeight="500">{label}</Text>
            <Text fontSize="sm" fontWeight="600" color="gray.800">{value || 'Not provided'}</Text>
        </Box>
    </HStack>
);

const SectionBox = ({ title, icon, children, ...props }) => (
    <Box
        borderWidth="1px"
        borderRadius="lg"
        p={4}
        bg="white"
        borderColor="gray.200"
        {...props}
    >
        <HStack mb={3} pb={2} borderBottom="1px solid" borderColor="gray.100">
            {icon && <Icon as={icon} color="brand.600" boxSize={5} />}
            <Heading size="xs" color="brand.700">{title}</Heading>
        </HStack>
        {children}
    </Box>
);

export const RegistrationPending = ({ registrationId: registrationIdProp } = {}) => {
    const params = useParams();
    // Support being rendered inside RegistrationHub (/register/:id) via prop,
    // or as a standalone route (/registration/:registrationID) via useParams.
    const registrationID = registrationIdProp || params.registrationID || params.id;
    const navigate = useNavigate();
    const toast = useToast();
    const { fullCertRegistrationPath } = useRouteConfig();
    const [registrationData, setRegistrationData] = useState(null);
    const [certificationRun, setCertificationRun] = useState(null);
    const [courseRun, setCourseRun] = useState(null); // for course-run (non–full-cert) registrations
    const [invoice, setInvoice] = useState(null);
    const [lineItems, setLineItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [showPayNowModal, setShowPayNowModal] = useState(false);
    const webhookSentRef = useRef(false); // Prevent duplicate webhook calls
    
    // Organization GST settings
    const [orgGstSettings, setOrgGstSettings] = useState({
        gst_enabled: false,
        gst_rate: 0
    });
    
    // Organization default contact email
    const [orgContactEmail, setOrgContactEmail] = useState('');

    const handlePayNowDone = async () => {
        setIsUpdatingStatus(true);
        try {
            const { error: updateError } = await supabase
                .from('registration')
                .update({ registration_stage: 'payment_in_progress' })
                .eq('id', registrationID);

            if (updateError) throw updateError;

            toast({
                title: "Payment Recorded",
                    description: "Your registration status has been updated to 'payment in progress'. We will verify the payment shortly.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            
            // Refresh data
            window.location.reload();
        } catch (err) {
            console.error('Error updating status:', err);
            toast({
                title: "Error",
                description: "Could not update status. Please contact support.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsUpdatingStatus(false);
            setShowPayNowModal(false);
        }
    };

    useEffect(() => {
        const fetchRegistration = async () => {
            setLoading(true);
            try {
                // Get registration details
                const { data: regData, error: regError } = await supabase
                    .from("registration")
                    .select("*, invoices!invoices_registration_id_fkey(*, invoice_line_items(*))")
                    .eq("id", registrationID)
                    .single();

                if (regError) throw regError;
                console.log(regData, 'regData');

                setRegistrationData(regData);

                // If there's a draft_cert_run_code, fetch certification run details
                let fetchedCertRun = null;
                if (regData.draft_cert_run_code) {
                    const { data: certRun, error: certError } = await supabase
                        .from("full_certification_runs")
                        .select("*, full_certification(*)")
                        .eq("id", regData.draft_cert_run_code)
                        .single();

                    if (!certError && certRun) {
                        setCertificationRun(certRun);
                        fetchedCertRun = certRun;
                    }
                }
                
                // Fetch organization GST settings
                try {
                    const { data: orgData, error: orgError } = await supabase
                        .from('organization')
                        .select('gst_enabled, gst_rate')
                        .limit(1)
                        .maybeSingle();
                    
                    if (!orgError && orgData) {
                        setOrgGstSettings({
                            gst_enabled: orgData.gst_enabled || false,
                            gst_rate: parseFloat(orgData.gst_rate) || 0
                        });
                        console.log('Organization GST settings loaded:', orgData);
                    }
                } catch (err) {
                    console.error('Error fetching organization GST settings:', err);
                }
                
                // Fetch organization default contact email
                try {
                    const { data: emailAccount, error: emailError } = await supabase
                        .from('organization_email_accounts')
                        .select('email_address')
                        .eq('is_default_for_email', true)
                        .eq('is_active', true)
                        .maybeSingle();
                    
                    if (!emailError && emailAccount?.email_address) {
                        setOrgContactEmail(emailAccount.email_address);
                        console.log('Organization contact email loaded:', emailAccount.email_address);
                    }
                } catch (err) {
                    console.error('Error fetching organization contact email:', err);
                }

                // If it's a course run (draft_course_run_code, not full cert), fetch course details for display
                if (regData.draft_course_run_code && !fetchedCertRun) {
                    const { data: cr, error: courseRunError } = await supabase
                        .from("course_run")
                        .select("id, start_date, end_date, course_type:course_type_code(name)")
                        .eq("id", regData.draft_course_run_code)
                        .single();
                    if (!courseRunError && cr) {
                        setCourseRun(cr);
                    }
                }

                // Pending approval + reg fee receipt: webhook and this page may both invoke; RPC serializes sends.
                if (regData.registration_stage === 'pending_approval') {
                    supabase.functions.invoke('send-registration-fee-received-email', {
                        body: { registrationId: registrationID },
                    }).then(({ error }) => {
                        if (error) console.error('send-registration-fee-received-email error:', error);
                    });
                    supabase.functions.invoke('send-pending-approval-email', {
                        body: { registrationId: registrationID },
                    }).then(({ error }) => {
                        if (error) console.error('send-pending-approval-email error:', error);
                    });
                }

            } catch (error) {
                console.error("Error fetching registration:", error);
                toast({
                    title: "Error",
                    description: "Could not load registration details.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchRegistration();
    }, [registrationID, toast]);

    // Handle Stripe checkout for registration payment
    const handleRegistrationPayment = async () => {
        if (!registrationData?.invoices?.[0]) {
            toast({
                title: "Error",
                description: "No invoice found for this registration.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        const invoice = registrationData.invoices[0];
        const invoiceSubtotal = invoice.invoice_line_items.reduce((acc, item) => acc + item.quantity * item.unit_price, 0) - invoice.amount_paid;
        
        // Apply GST to registration fee if enabled (same as course fees)
        const gstRate = orgGstSettings.gst_enabled ? (orgGstSettings.gst_rate / 100) : 0;
        const gstAmount = invoiceSubtotal * gstRate;
        const totalWithGst = invoiceSubtotal + gstAmount;

        setIsProcessingPayment(true);

        try {
            toast({
                title: "Redirecting to payment",
                description: "Please wait while we redirect you to the secure payment page...",
                status: "info",
                duration: 3000,
                isClosable: true,
            });

            // Prepare data for the edge function - send total including GST
            const requestData = {
                registration_id: registrationID,
                participant_email: registrationData.participant_email,
                participant_name: registrationData.participant_name,
                invoice_id: invoice.id,
                invoice_balance: totalWithGst, // Total including GST
                registration_fee_subtotal: invoiceSubtotal, // Pre-GST amount for reference
                gst_amount: gstAmount, // GST amount for reference
                gst_rate: orgGstSettings.gst_rate, // GST rate for reference
                gst_enabled: orgGstSettings.gst_enabled, // GST enabled flag
                invoice_line_items: invoice.invoice_line_items || [],
                course_name: certificationRun?.full_certification?.name || 'Course Registration',
                course_start_date: certificationRun?.start_date 
                    ? moment(certificationRun.start_date).format('DD MMM YYYY') 
                    : null
            };

            console.log('Calling create-registration-checkout with data:', requestData);

            // Call the Supabase Edge Function
            const { data, error } = await supabase.functions.invoke("create-registration-checkout", {
                body: requestData
            });

            console.log('Edge Function response:', { data, error });

            if (error) {
                console.error('Edge Function error:', error);
                throw new Error(`Failed to create checkout session: ${error.message}`);
            }

            if (!data?.url) {
                console.error('Invalid response format:', data);
                throw new Error("Checkout URL not found in the response.");
            }

            // Redirect to Stripe checkout
            window.location.href = data.url;

        } catch (error) {
            console.error('Error processing payment:', error);
            toast({
                title: "Payment Error",
                description: error.message || "Failed to process payment. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            setIsProcessingPayment(false);
        }
    };

    if (loading) {
        return (
            <Center minH="100vh" bg="gray.50">
                <VStack spacing={4}>
                    <Spinner size="xl" color="brand.500" thickness="4px" />
                    <Text color="gray.600">Loading registration details...</Text>
                </VStack>
            </Center>
        );
    }

    if (!registrationData) {
        return (
            <Center minH="100vh" bg="gray.50">
                <VStack spacing={4}>
                    <Text color="gray.600" fontSize="lg">Registration not found.</Text>
                    <Button
                        leftIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/')}
                        colorScheme="brand"
                    >
                        Go Home
                    </Button>
                </VStack>
            </Center>
        );
    }

    const renderFieldValue = (fieldValue, fieldKey = '') => {
        // Handle null/undefined
        if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
            return 'Not provided';
        }

        // Handle base64 images (signatures)
        if (typeof fieldValue === 'string' && fieldValue.startsWith('data:image/')) {
            return (
                <Box mt={1} p={2} bg="white" borderRadius="md" border="1px solid" borderColor="gray.100" display="inline-block">
                    <img 
                        src={fieldValue} 
                        alt="Signature" 
                        style={{ maxHeight: '80px', width: 'auto', display: 'block' }} 
                    />
                </Box>
            );
        }

        // Handle file paths (Supabase storage paths)
        const isFilePath = (val) => typeof val === 'string' && (val.startsWith('registrations/') || val.startsWith('full__cert/'));
        
        const renderFile = (path) => {
            const fileName = path.split('/').pop();
            const { data: urlData } = supabase.storage.from('Registration_data').getPublicUrl(path);
            const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);

            if (isImage) {
                return (
                    <Box mt={2} p={2} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100" display="inline-block">
                        <Box
                            as="img"
                            src={urlData.publicUrl}
                            alt="Preview"
                            maxH="120px"
                            borderRadius="sm"
                            border="1px solid"
                            borderColor="gray.200"
                            fallbackSrc="https://via.placeholder.com/120?text=Image"
                            cursor="pointer"
                            onClick={() => window.open(urlData.publicUrl, '_blank')}
                        />
                    </Box>
                );
            }

            return (
                <Box mt={2} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100" display="inline-block">
                    <HStack spacing={2} cursor="pointer" onClick={() => window.open(urlData.publicUrl, '_blank')}>
                        <Icon as={HiGlobeAlt} color="blue.500" />
                        <Text fontSize="xs" fontWeight="bold" color="blue.600">View Document</Text>
                    </HStack>
                </Box>
            );
        };

        if (isFilePath(fieldValue)) {
            return renderFile(fieldValue);
        }

        // Handle arrays
        if (Array.isArray(fieldValue)) {
            if (fieldValue.length === 0) return 'Not provided';
            
            // Check if array contains file paths
            if (isFilePath(fieldValue[0])) {
                return (
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2} mt={1}>
                        {fieldValue.map((path, idx) => (
                            <Box key={idx}>{renderFile(path)}</Box>
                        ))}
                    </SimpleGrid>
                );
            }

            // Check if array contains objects (like education/work history)
            if (typeof fieldValue[0] === 'object') {
                return (
                    <VStack align="stretch" spacing={2} mt={1}>
                        {fieldValue.map((item, idx) => (
                            <Box key={idx} p={3} bg="gray.50" borderRadius="md" fontSize="xs" borderLeft="3px solid" borderColor="brand.200">
                                <SimpleGrid columns={2} spacing={2}>
                                    {Object.entries(item).map(([key, value]) => (
                                        <Box key={key}>
                                            <Text fontWeight="600" color="gray.500" textTransform="uppercase" fontSize="9px" letterSpacing="wider">
                                                {key.replace(/_/g, ' ')}
                                            </Text>
                                            <Text color="gray.800" fontWeight="500">{String(value || 'N/A')}</Text>
                                        </Box>
                                    ))}
                                </SimpleGrid>
                            </Box>
                        ))}
                    </VStack>
                );
            }
            
            // Array of primitives
            return fieldValue.join(', ');
        }

        // Handle objects (nested data)
        if (typeof fieldValue === 'object') {
            return (
                <Box p={3} bg="gray.50" borderRadius="md" mt={1}>
                    <SimpleGrid columns={2} spacing={3}>
                        {Object.entries(fieldValue).map(([key, value]) => (
                            <Box key={key}>
                                <Text fontWeight="600" color="gray.500" textTransform="uppercase" fontSize="9px" letterSpacing="wider">
                                    {key.replace(/_/g, ' ')}
                                </Text>
                                <Text color="gray.800" fontSize="xs" fontWeight="500">{String(value || 'N/A')}</Text>
                            </Box>
                        ))}
                    </SimpleGrid>
                </Box>
            );
        }

        // Handle booleans
        if (typeof fieldValue === 'boolean') {
            return fieldValue ? 'Yes' : 'No';
        }

        // Handle dates
        if (typeof fieldValue === 'string' && moment(fieldValue, moment.ISO_8601, true).isValid()) {
            return moment(fieldValue).format('DD MMM YYYY');
        }

        // Default: convert to string
        return String(fieldValue);
    };

    const renderAdditionalFields = (data) => {
        if (!data || typeof data !== 'object') return null;

        const sections = {};
        
        // Group fields by section
        Object.keys(data).forEach(key => {
            if (typeof data[key] === 'object' && data[key] !== null && !Array.isArray(data[key])) {
                sections[key] = data[key];
            } else {
                // Handle top-level fields not in a section
                if (!sections['General Information']) {
                    sections['General Information'] = {};
                }
                sections['General Information'][key] = data[key];
            }
        });

        if (Object.keys(sections).length === 0) return null;

        return (
            <Accordion allowMultiple defaultIndex={[0]} variant="separated">
                {Object.entries(sections).map(([sectionKey, sectionData], index) => {
                    const fields = Object.entries(sectionData)
                        .filter(([key]) => {
                            const lowKey = key.toLowerCase();
                            return !lowKey.includes('referral') && !lowKey.includes('promo');
                        })
                        .sort(([keyA], [keyB]) => {
                            // Move Photo to the top
                            if (keyA === 'documents_photograph_url') return -1;
                            if (keyB === 'documents_photograph_url') return 1;
                            
                            // Move Education Certificate right after Education
                            if (keyA === 'education' && keyB === 'documents_educational_certificate_url') return -1;
                            if (keyA === 'documents_educational_certificate_url' && keyB === 'education') return 1;
                            
                            return 0;
                        });

                    if (fields.length === 0) return null;

                    return (
                        <AccordionItem 
                            key={index} 
                            border="1px solid" 
                            borderColor="gray.200" 
                            borderRadius="lg" 
                            mb={4} 
                            overflow="hidden"
                            bg="white"
                            boxShadow="sm"
                        >
                            <AccordionButton
                                _expanded={{ bg: "brand.50", borderBottom: "1px solid", borderColor: "brand.100" }}
                                _hover={{ bg: "gray.50" }}
                                px={5}
                                py={4}
                            >
                                <Box flex="1" textAlign="left">
                                    <HStack spacing={3}>
                                        <Box bg="brand.100" p={1.5} borderRadius="md">
                                            <Icon as={HiInformationCircle} color="brand.600" boxSize={4} />
                                        </Box>
                                        <Text fontWeight="700" color="gray.800" fontSize="sm" textTransform="uppercase" letterSpacing="wide">
                                            {sectionKey.replace(/_/g, ' ')}
                                        </Text>
                                    </HStack>
                                </Box>
                                <AccordionIcon color="brand.500" />
                            </AccordionButton>
                            <AccordionPanel pb={6} pt={4} px={6}>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={6}>
                                    {fields.map(([fieldKey, fieldValue]) => {
                                        // Dynamic field labels
                                        let label = fieldKey.replace(/_/g, ' ');
                                        
                                        if (fieldKey === 'personal_details_gender') {
                                            label = 'Gender';
                                        } else if (fieldKey === 'documents_nric_verification_url') {
                                            const nationality = registrationData.nationality || '';
                                            if (nationality.includes('FIN')) {
                                                label = 'FIN Verification';
                                            } else if (nationality.includes('Passport')) {
                                                label = 'Passport Verification';
                                            } else {
                                                label = 'NRIC Verification';
                                            }
                                        } else if (fieldKey === 'documents_fin_passport_verification_url') {
                                            label = 'Passport Verification';
                                        } else if (fieldKey === 'legal_declaration_signature_url' || fieldKey === 'signature') {
                                            label = 'Signature';
                                        } else if (fieldKey === 'personal_details_work_status') {
                                            label = 'Work Status';
                                        } else if (fieldKey === 'documents_photograph_url') {
                                            label = 'Photo';
                                        } else if (fieldKey === 'documents_educational_certificate_url') {
                                            label = 'Education Certificate';
                                        }

                                        return (
                                            <Box key={fieldKey}>
                                                <Text fontSize="xs" color="gray.400" fontWeight="700" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                                                    {label}
                                                </Text>
                                                <Box>
                                                    {renderFieldValue(fieldValue, fieldKey)}
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </SimpleGrid>
                            </AccordionPanel>
                        </AccordionItem>
                    );
                })}
            </Accordion>
        );
    };

    // Stage-aware display config
    const stageConfig = (() => {
        const stage = registrationData?.registration_stage;
        switch (stage) {
            case 'pending_approval':
                return {
                    borderColor: 'yellow.200',
                    iconBg: 'yellow.100',
                    iconColor: 'yellow.500',
                    heading: 'Awaiting Admin Approval',
                    subheading: 'Your registration has been received and is pending review by our team.',
                    alertStatus: 'warning',
                    alertTitle: 'Under Review',
                    alertBody: `Our team is reviewing your application. You will be notified at ${registrationData?.participant_email} once a decision has been made.`,
                    badgeLabel: 'Awaiting Approval',
                    badgeColor: 'yellow',
                };
            case 'amended':
                return {
                    borderColor: 'blue.200',
                    iconBg: 'blue.100',
                    iconColor: 'blue.500',
                    heading: 'Application Updated',
                    subheading: 'Your updated registration has been submitted and is under review.',
                    alertStatus: 'info',
                    alertTitle: 'Resubmitted — Under Review',
                    alertBody: `Your amended application is being reviewed. We'll contact you at ${registrationData?.participant_email} with next steps.`,
                    badgeLabel: 'Amended — Under Review',
                    badgeColor: 'blue',
                };
            case 'cancelled':
                return {
                    borderColor: 'red.200',
                    iconBg: 'red.100',
                    iconColor: 'red.400',
                    heading: 'Registration Cancelled',
                    subheading: 'This registration has been cancelled.',
                    alertStatus: 'error',
                    alertTitle: 'Cancelled',
                    alertBody: `If you believe this is a mistake, please contact us at ${registrationData?.participant_email}.`,
                    badgeLabel: 'Cancelled',
                    badgeColor: 'red',
                };
            default:
                // 'pending' and any other stage
                return {
                    borderColor: 'green.200',
                    iconBg: 'green.100',
                    iconColor: 'green.500',
                    heading: 'Registration Received!',
                    subheading: 'Thank you for submitting your registration. Please review your information below.',
                    alertStatus: 'info',
                    alertTitle: "What's Next?",
                    alertBody: `We'll review your registration and contact you at ${registrationData?.participant_email} or ${registrationData?.mobile_no} with further instructions.`,
                    badgeLabel: 'Pending Review',
                    badgeColor: 'gray',
                };
        }
    })();

    return (
        <Box minH="100vh" bg="gray.50" py={8}>
            <Container maxW="container.lg">
                <MotionBox
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {/* Stage-aware Header */}
                    <Box
                        bg="white"
                        borderRadius="xl"
                        p={8}
                        textAlign="center"
                        boxShadow="lg"
                        borderWidth="1px"
                        borderColor={stageConfig.borderColor}
                        mb={6}
                    >
                        <VStack spacing={4}>
                            <Box
                                bg={stageConfig.iconBg}
                                borderRadius="full"
                                p={4}
                            >
                                <CheckCircleIcon boxSize={12} color={stageConfig.iconColor} />
                            </Box>
                            <Heading size="md" color="gray.800">
                                {stageConfig.heading}
                            </Heading>
                            <Text color="gray.600" fontSize="md">
                                {stageConfig.subheading}
                            </Text>
                        </VStack>
                    </Box>

                    {/* Stage-aware next-steps alert */}
                    <Alert status={stageConfig.alertStatus} mb={6} borderRadius="lg">
                        <AlertIcon />
                        <Box>
                            <Text fontWeight="600" mb={1}>{stageConfig.alertTitle}</Text>
                            <Text fontSize="sm">{stageConfig.alertBody}</Text>
                        </Box>
                    </Alert>

                    {/* Certification Details (full cert) */}
                    {certificationRun && (
                        <SectionBox title="Program Details" icon={HiGlobeAlt} mb={6}>
                            <VStack align="stretch" spacing={3}>
                                <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">Program</Text>
                                    <Text fontSize="md" fontWeight="700" color="brand.700">
                                        {certificationRun.full_certification?.name}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">Run</Text>
                                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                                        {certificationRun.run_name}
                                    </Text>
                                    <Text fontSize="xs" color="gray.600">
                                        {moment(certificationRun.start_date).format('DD MMM YYYY')} - {moment(certificationRun.end_date).format('DD MMM YYYY')}
                                    </Text>
                                </Box>
                                {certificationRun.course_run && certificationRun.course_run.length > 0 && (
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" fontWeight="500" mb={2}>Included Modules</Text>
                                        <VStack align="stretch" spacing={1}>
                                            {certificationRun.course_run.map((course, idx) => (
                                                <HStack key={idx} spacing={2}>
                                                    <Badge colorScheme="blue" fontSize="xs">{course.course_type_code}</Badge>
                                                    <Text fontSize="xs" color="gray.700">{course.course_type?.name}</Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    </Box>
                                )}
                            </VStack>
                        </SectionBox>
                    )}

                    {/* Course details (course run, non–full-cert) */}
                    {courseRun && !certificationRun && (
                        <SectionBox title="Course Details" icon={HiGlobeAlt} mb={6}>
                            <VStack align="stretch" spacing={3}>
                                <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">Course</Text>
                                    <Text fontSize="md" fontWeight="700" color="brand.700">
                                        {courseRun.course_type?.name || 'Course'}
                                    </Text>
                                </Box>
                                <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">Dates</Text>
                                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                                        {courseRun.start_date && courseRun.end_date
                                            ? `${moment(courseRun.start_date).format('DD MMM YYYY')} – ${moment(courseRun.end_date).format('DD MMM YYYY')}`
                                            : 'To be confirmed'}
                                    </Text>
                                </Box>
                            </VStack>
                        </SectionBox>
                    )}

                    {/* Personal Information */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
                        <SectionBox title="Personal Information" icon={HiUser}>
                            <VStack align="stretch" spacing={1} divider={<Divider />}>
                                <InfoRow icon={HiUser} label="Full Name" value={registrationData.participant_name} />
                                <InfoRow icon={HiMail} label="Email" value={registrationData.participant_email} />
                                <InfoRow icon={HiPhone} label="Mobile Number" value={registrationData.mobile_no} />
                                <InfoRow icon={HiIdentification} label="NRIC" value={registrationData.nric} />
                                <InfoRow 
                                    icon={HiCalendar} 
                                    label="Date of Birth" 
                                    value={registrationData.dob ? moment(registrationData.dob).format('DD MMM YYYY') : null} 
                                />
                                <InfoRow label="Age" value={registrationData.birth_age} />
                                <InfoRow label="Identification Type" value={registrationData.nationality} />
                            </VStack>
                        </SectionBox>

                        <SectionBox title="Registration Status" icon={CheckCircleIcon}>
                            <VStack align="stretch" spacing={3}>
                                <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">Status</Text>
                                    <Badge colorScheme={stageConfig.badgeColor} fontSize="sm">
                                        {stageConfig.badgeLabel}
                                    </Badge>
                                </Box>
                                <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500">Submitted On</Text>
                                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                                        {moment(registrationData.created_at).format('DD MMM YYYY, h:mm A')}
                                    </Text>
                                </Box>

                                {/* Invoice Details */}
                                {registrationData.invoices[0] && (() => {
                                    const inv = registrationData.invoices[0];
                                    const regFeeSubtotal = (inv.invoice_line_items || []).reduce((acc, item) => acc + (item.quantity || 0) * (item.unit_price || 0), 0);
                                    const isWaived = regFeeSubtotal === 0;
                                    return (
                                        <>
                                            <Box pt={3} borderTop="1px solid" borderColor="gray.200">
                                                <HStack justify="space-between" mb={2}>
                                                    <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase">
                                                        Invoice Details
                                                    </Text>
                                                    <Badge 
                                                        colorScheme={
                                                            isWaived ? 'green' :
                                                            inv.status === 'paid' ? 'green' : 
                                                            inv.status === 'pending' ? 'yellow' : 
                                                            'red'
                                                        } 
                                                        fontSize="xs"
                                                    >
                                                        {isWaived ? 'Waived' : inv.status}
                                                    </Badge>
                                                </HStack>
                                                {isWaived ? (
                                                    <Text fontSize="sm" color="gray.600" py={2}>
                                                        Registration fee has been waived.
                                                    </Text>
                                                ) : (
                                                    <>
                                                        {/* Line Items */}
                                                        {inv.invoice_line_items && inv.invoice_line_items.length > 0 && (
                                                            <>
                                                                <VStack align="stretch" spacing={1} mb={2}>
                                                                    {inv.invoice_line_items.map((item, idx) => (
                                                                        <HStack key={idx} justify="space-between" fontSize="xs">
                                                                            <Text color="gray.600">{item.description === 'Full Certification Registration Fee' ? 'Registration Fee' : item.description}</Text>
                                                                            <Text fontWeight="600" color="gray.800">
                                                                                S$ {(item.quantity * (item.unit_price || 0)).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                            </Text>
                                                                        </HStack>
                                                                    ))}
                                                                </VStack>
                                                                {(() => {
                                                                    const gstRate = orgGstSettings.gst_enabled ? (orgGstSettings.gst_rate / 100) : 0;
                                                                    const gstAmount = regFeeSubtotal * gstRate;
                                                                    const total = regFeeSubtotal + gstAmount;
                                                                    const amountPaid = inv.amount_paid || 0;
                                                                    const balanceDue = total - amountPaid;
                                                                    return (
                                                                        <VStack align="stretch" spacing={1} pt={2} borderTop="1px solid" borderColor="gray.100">
                                                                            <HStack justify="space-between" fontSize="xs">
                                                                                <Text color="gray.600">Subtotal:</Text>
                                                                                <Text fontWeight="600" color="gray.800">
                                                                                    S$ {regFeeSubtotal.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                </Text>
                                                                            </HStack>
                                                                            {orgGstSettings.gst_enabled && gstAmount > 0 && (
                                                                                <HStack justify="space-between" fontSize="xs">
                                                                                    <Text color="gray.600">GST ({orgGstSettings.gst_rate}%):</Text>
                                                                                    <Text fontWeight="600" color="gray.800">
                                                                                        S$ {gstAmount.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                    </Text>
                                                                                </HStack>
                                                                            )}
                                                                            <HStack justify="space-between" fontSize="xs" pt={1}>
                                                                                <Text color="gray.700" fontWeight="600">Total:</Text>
                                                                                <Text fontWeight="700" color="gray.800">
                                                                                    S$ {total.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                </Text>
                                                                            </HStack>
                                                                            {amountPaid > 0 && (
                                                                                <>
                                                                                    <HStack justify="space-between" fontSize="xs">
                                                                                        <Text color="gray.600">Amount Paid:</Text>
                                                                                        <Text fontWeight="600" color="green.600">
                                                                                            S$ {amountPaid.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                        </Text>
                                                                                    </HStack>
                                                                                    <HStack justify="space-between" fontSize="xs">
                                                                                        <Text color="gray.600" fontWeight="600">Balance Due:</Text>
                                                                                        <Text fontWeight="700" color="brand.600">
                                                                                            S$ {balanceDue.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                                        </Text>
                                                                                    </HStack>
                                                                                </>
                                                                            )}
                                                                            {inv.due_date && (
                                                                                <Box mt={2}>
                                                                                    <Text fontSize="xs" color="gray.500">Due Date:</Text>
                                                                                    <Text fontSize="xs" fontWeight="600" color="gray.700">
                                                                                        {moment(inv.due_date).format('DD MMM YYYY')}
                                                                                    </Text>
                                                                                </Box>
                                                                            )}
                                                                        </VStack>
                                                                    );
                                                                })()}
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </>
                                    );
                                })()}
                            </VStack>
                        </SectionBox>
                    </SimpleGrid>

                    {/* Additional Information */}
                    {registrationData.additional_data && Object.keys(registrationData.additional_data).length > 0 && (
                        <SectionBox title="Additional Information" icon={HiInformationCircle} mb={6}>
                            {renderAdditionalFields(registrationData.additional_data)}
                        </SectionBox>
                    )}

                    {/* Payment Options Section - only show when registration fee > 0 */}
                    {registrationData.invoices?.[0] &&
                     (registrationData.registration_stage === 'approved' || registrationData.registration_stage === 'pending') &&
                     registrationData.invoices[0].status === 'pending payment' &&
                     (registrationData.invoices[0].invoice_line_items || []).reduce((acc, item) => acc + (item.quantity || 0) * (item.unit_price || 0), 0) > 0 && (
                        <SectionBox title="Payment Options" icon={HiCreditCard} mb={6} borderColor="brand.300" borderWidth="2px">
                            <VStack spacing={4} align="stretch">
                                {(() => {
                                    // Calculate registration fee with GST
                                    const registrationFee = registrationData.invoices[0].invoice_line_items.reduce((acc, item) => acc + item.quantity * item.unit_price, 0);
                                    const gstRate = orgGstSettings.gst_enabled ? (orgGstSettings.gst_rate / 100) : 0;
                                    const gstAmount = registrationFee * gstRate;
                                    const totalWithGst = registrationFee + gstAmount;
                                    
                                    return (
                                        <VStack spacing={2} py={3} px={4} bg="brand.50" borderRadius="md" border="1px solid" borderColor="brand.200">
                                            <Text fontSize="xs" fontWeight="bold" color="brand.600" textTransform="uppercase" letterSpacing="wider">Registration Fee Due</Text>
                                            <Box bg="white" p={3} borderRadius="md" w="100%" minW="200px">
                                                <VStack spacing={2} align="stretch">
                                                    <HStack justify="space-between">
                                                        <Text fontSize="sm" color="gray.600">Registration Fee:</Text>
                                                        <Text fontSize="sm" fontWeight="medium">S$ {registrationFee.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                                                    </HStack>
                                                    {orgGstSettings.gst_enabled && gstAmount > 0 && (
                                                        <HStack justify="space-between">
                                                            <Text fontSize="sm" color="gray.600">GST ({orgGstSettings.gst_rate}%):</Text>
                                                            <Text fontSize="sm" fontWeight="medium">S$ {gstAmount.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                                                        </HStack>
                                                    )}
                                                    <Divider />
                                                    <HStack justify="space-between" align="center">
                                                        <Text fontSize="md" fontWeight="bold" color="gray.700">Total:</Text>
                                                        <Text fontSize="xl" fontWeight="bold" color="brand.700">S$ {totalWithGst.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                                                    </HStack>
                                                </VStack>
                                            </Box>
                                        </VStack>
                                    );
                                })()}
                                
                                <VStack spacing={6} w="100%">
                                    {/* Option 1: PayNow Transfer - Most Prominent */}
                                    <Box 
                                        w="100%"
                                        p={6}
                                        borderWidth="2px"
                                        borderRadius="xl"
                                        bg="white"
                                        borderColor="green.300"
                                        boxShadow="md"
                                        _hover={{ borderColor: "green.400", boxShadow: "lg" }}
                                        transition="all 0.2s"
                                    >
                                        <VStack spacing={4} align="stretch">
                                            <HStack spacing={3} align="center">
                                                <Icon as={HiQrcode} boxSize={6} color="green.600" />
                                                <Text fontWeight="bold" color="green.700" fontSize="lg">PayNow Transfer</Text>
                                                <Badge colorScheme="green" ml="auto">Fastest</Badge>
                                            </HStack>
                                            
                                            <Box bg="green.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="green.200">
                                                <VStack spacing={3} align="center">
                                                    <Box bg="white" p={3} borderRadius="md" boxShadow="sm">
                                                        <img 
                                                            src="/paynow-qr.png" 
                                                            alt="PayNow QR Code" 
                                                            id="paynow-qr-image"
                                                            style={{ width: '180px', height: 'auto', display: 'block' }}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = "https://via.placeholder.com/180x180?text=PayNow+QR";
                                                            }}
                                                        />
                                                    </Box>
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline" 
                                                        colorScheme="green"
                                                        leftIcon={<Icon as={HiDocumentText} />}
                                                        onClick={() => {
                                                            const link = document.createElement('a');
                                                            link.href = '/paynow-qr.png';
                                                            link.download = `PayNow-QR-${registrationID.substring(0, 8)}.png`;
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                        }}
                                                    >
                                                        Download QR Code
                                                    </Button>
                                                </VStack>
                                            </Box>

                                            <VStack spacing={2} align="stretch" fontSize="sm">
                                                <Box bg="gray.50" p={3} borderRadius="md">
                                                    <Text fontWeight="semibold" color="gray.700" mb={1}>UEN No:</Text>
                                                    <Text color="gray.800" fontFamily="mono" fontSize="md">201940174R</Text>
                                                </Box>
                                                <Box bg="gray.50" p={3} borderRadius="md">
                                                    <Text fontWeight="semibold" color="gray.700" mb={1}>Beneficiary Name:</Text>
                                                    <Text color="gray.800">Edusphere College of Management and Technology Pte Ltd</Text>
                                                </Box>
                                                <Box bg="green.100" p={3} borderRadius="md" borderWidth="1px" borderColor="green.300">
                                                    <Text fontWeight="bold" color="green.800" mb={1}>Reference Number:</Text>
                                                    <Text color="green.900" fontFamily="mono" fontSize="lg" fontWeight="bold">
                                                        {registrationID?.substring(0, 8).toUpperCase()}
                                                    </Text>
                                                    <Text fontSize="xs" color="gray.600" mt={1}>
                                                        Please include this reference when making payment
                                                    </Text>
                                                </Box>
                                            </VStack>

                                            <Button 
                                                size="lg" 
                                                colorScheme="green" 
                                                w="100%"
                                                onClick={handlePayNowDone}
                                                isLoading={isUpdatingStatus}
                                                leftIcon={<Icon as={HiQrcode} />}
                                            >
                                                I have paid via PayNow
                                            </Button>
                                        </VStack>
                                    </Box>

                                    {/* Option 2: Bank Transfer - Collapsible Details */}
                                    <Box 
                                        w="100%"
                                        p={6}
                                        borderWidth="2px"
                                        borderRadius="xl"
                                        bg="white"
                                        borderColor="purple.300"
                                        boxShadow="md"
                                        _hover={{ borderColor: "purple.400", boxShadow: "lg" }}
                                        transition="all 0.2s"
                                    >
                                        <VStack spacing={4} align="stretch">
                                            <HStack spacing={3} align="center">
                                                <Icon as={HiOfficeBuilding} boxSize={6} color="purple.600" />
                                                <Text fontWeight="bold" color="purple.700" fontSize="lg">Bank Transfer</Text>
                                            </HStack>

                                            <Accordion allowToggle defaultIndex={0}>
                                                <AccordionItem border="none">
                                                    <AccordionButton px={0} py={2} _hover={{ bg: "transparent" }}>
                                                        <Box flex="1" textAlign="left">
                                                            <Text fontWeight="semibold" color="gray.700">
                                                                View Bank Account Details
                                                            </Text>
                                                        </Box>
                                                        <AccordionIcon />
                                                    </AccordionButton>
                                                    <AccordionPanel px={0} pb={4}>
                                                        <VStack spacing={3} align="stretch" fontSize="sm">
                                                            <SimpleGrid columns={2} spacing={3}>
                                                                <Box bg="gray.50" p={3} borderRadius="md">
                                                                    <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Bank Name</Text>
                                                                    <Text color="gray.800" fontWeight="medium">OCBC</Text>
                                                                </Box>
                                                                <Box bg="gray.50" p={3} borderRadius="md">
                                                                    <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Bank/Branch Code</Text>
                                                                    <Text color="gray.800" fontFamily="mono" fontWeight="medium">7339</Text>
                                                                </Box>
                                                                <Box bg="gray.50" p={3} borderRadius="md">
                                                                    <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">SWIFT Code</Text>
                                                                    <Text color="gray.800" fontFamily="mono" fontWeight="medium">OCBCSGSG</Text>
                                                                </Box>
                                                                <Box bg="gray.50" p={3} borderRadius="md">
                                                                    <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Account Type</Text>
                                                                    <Text color="gray.800" fontWeight="medium">Current Account</Text>
                                                                </Box>
                                                            </SimpleGrid>
                                                            <Box bg="purple.50" p={3} borderRadius="md" borderWidth="1px" borderColor="purple.200">
                                                                <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Account Number</Text>
                                                                <Text color="purple.800" fontFamily="mono" fontSize="lg" fontWeight="bold">
                                                                    519-580831-001
                                                                </Text>
                                                            </Box>
                                                            <Box bg="gray.50" p={3} borderRadius="md">
                                                                <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="xs">Beneficiary Name</Text>
                                                                <Text color="gray.800">Edusphere College of Management and Technology Pte Ltd</Text>
                                                            </Box>
                                                            <Box bg="purple.100" p={3} borderRadius="md" borderWidth="1px" borderColor="purple.300">
                                                                <Text fontWeight="bold" color="purple.800" mb={1} fontSize="xs">Reference Number</Text>
                                                                <Text color="purple.900" fontFamily="mono" fontSize="lg" fontWeight="bold">
                                                                    {registrationID?.substring(0, 8).toUpperCase()}
                                                                </Text>
                                                                <Text fontSize="xs" color="gray.600" mt={1}>
                                                                    Please include this reference when making payment
                                                                </Text>
                                                            </Box>
                                                        </VStack>
                                                    </AccordionPanel>
                                                </AccordionItem>
                                            </Accordion>

                                            <Button 
                                                size="lg" 
                                                colorScheme="purple" 
                                                w="100%"
                                                onClick={handlePayNowDone}
                                                isLoading={isUpdatingStatus}
                                                leftIcon={<Icon as={HiOfficeBuilding} />}
                                            >
                                                I have paid via Bank Transfer
                                            </Button>
                                        </VStack>
                                    </Box>

                                    {/* Option 3: Stripe - Simple and Clear */}
                                    <Box 
                                        w="100%"
                                        p={6}
                                        borderWidth="2px"
                                        borderRadius="xl"
                                        bg="white"
                                        borderColor="blue.300"
                                        boxShadow="md"
                                        _hover={{ borderColor: "blue.400", boxShadow: "lg" }}
                                        transition="all 0.2s"
                                    >
                                        <VStack spacing={4} align="stretch">
                                            <HStack spacing={3} align="center">
                                                <Icon as={HiCreditCard} boxSize={6} color="blue.600" />
                                                <Text fontWeight="bold" color="blue.700" fontSize="lg">Stripe</Text>
                                                <Badge colorScheme="blue" ml="auto">Secure</Badge>
                                            </HStack>
                                            
                                            <Box bg="blue.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="blue.200">
                                                <VStack spacing={2} align="center">
                                                    <Icon as={HiCreditCard} boxSize={10} color="blue.500" />
                                                    <Text fontSize="sm" color="gray.700" textAlign="center">
                                                        Pay securely with your credit or debit card
                                                    </Text>
                                                </VStack>
                                            </Box>

                                            <Button 
                                                size="lg" 
                                                colorScheme="blue" 
                                                w="100%"
                                                onClick={handleRegistrationPayment}
                                                isLoading={isProcessingPayment}
                                                leftIcon={<Icon as={HiCreditCard} />}
                                            >
                                                Pay via Stripe
                                            </Button>
                                        </VStack>
                                    </Box>
                                </VStack>
                            </VStack>
                        </SectionBox>
                    )}

                    {/* Action Buttons */}
                    <Stack direction={{ base: 'column', md: 'row' }} spacing={4} justify="center">
                        {/* Only show Edit Registration button when registration is in draft or rejected stage */}
                        {(registrationData.registration_stage === 'draft' || registrationData.registration_stage === 'rejected') && (
                            <Button
                                leftIcon={<EditIcon />}
                                onClick={() => navigate(`/register/${registrationID}`)}
                                colorScheme="orange"
                                size="lg"
                                variant="outline"
                            >
                                Edit Registration
                            </Button>
                        )}
                    </Stack>

                    {/* Footer Note */}
                    <Box mt={8} p={4} bg="blue.50" borderRadius="lg" textAlign="center">
                        <Text fontSize="sm" color="blue.800">
                            If you have any questions, please contact us at{' '}
                            {orgContactEmail ? (
                                <Text as="a" href={`mailto:${orgContactEmail}`} fontWeight="bold" _hover={{ textDecoration: 'underline' }}>
                                    {orgContactEmail}
                                </Text>
                            ) : (
                                <Text as="span" fontWeight="bold">our support team</Text>
                            )}
                        </Text>
                    </Box>
                </MotionBox>
            </Container>
        </Box>
    );
};

export default RegistrationPending;

